import React, { useMemo, useRef, useState } from 'react';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, CircularProgress, Typography } from '@mui/material';

import { getAnswerAttributes } from '@host/components/NewUI/Application/SmartFormsTab/get-answer-attributes';
import { getAnswerValue } from '@host/components/NewUI/Application/SmartFormsTab/get-answer-value';
import { validateForm } from '@host/components/NewUI/Application/SmartFormsTab/validate-form-submission';
import QuestionFieldGenerator from '@host/components/NewUI/Components/Utilities/QuestionFieldGenerator';
import { Button } from '@host/components/NewUI/ComponentsLibrary';

import Layout from './components/Layout';
import PortalBackButton from './components/PortalBackButton';
import PortalCard from './components/PortalCard';
import PortalFooter from './components/PortalFooter';
import PortalSelectQuestion from './components/PortalSelectQuestion';
import { font, palette } from './styles';
import { triggerDownload } from './usePortalDownloads';
import { onboardColors } from '../theme/onboardTokens';

import type { IAnswer } from '@host/components/NewUI/ApprovalForms/types';
import type { IApplicantQuestion } from '@host/components/NewUI/Components/Utilities/QuestionFieldGenerator';

import type { IPortalItemRow } from './types';

interface Props {
  item: IPortalItemRow;
  formId: number | null;
  answerRows: IAnswer[];
  loading: boolean;
  questions: IApplicantQuestion[];
  downloadUrl: string;
  onBack: () => void;
  onSubmit: (payload: {
    form_id: number;
    answers_attributes: Record<number, unknown>;
  }) => Promise<void>;
}

// Outlined MUI inputs (e.g. the date picker) default to a darker outline than the
// host's text and select fields; match them to the #DDDDDD those fields use.
const AT_REST = ':not(.Mui-focused):not(.Mui-error):not(.Mui-disabled)';
const FIELD_BORDER_SX = {
  [[
    `& .MuiPickersOutlinedInput-root${AT_REST} .MuiPickersOutlinedInput-notchedOutline`,
    `& .MuiOutlinedInput-root${AT_REST} .MuiOutlinedInput-notchedOutline`
  ].join(', ')]: { borderColor: onboardColors.border.divider },
  // The host date picker style nudges its icon button up 1.5px, which leaves the
  // calendar icon above centre in this field; keep only the horizontal nudge.
  '& .MuiPickersOutlinedInput-root button': { transform: 'translateX(-4px)' }
};

const SmartForm: React.FC<Props> = ({
  item,
  formId,
  answerRows,
  loading,
  questions,
  downloadUrl,
  onBack,
  onSubmit
}) => {
  const [answers, setAnswers] = useState<
    Record<number, string | number | Record<string, string | boolean>>
  >({});
  const [questionErrors, setQuestionErrors] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const questionsRef = useRef<(HTMLDivElement | null)[]>([]);
  const readOnly = item.status === 'submitted';

  const downloadForm = () => triggerDownload(downloadUrl, item.label);

  const setAnswer = (id: number, value: string | number | Record<string, string | boolean>) =>
    setAnswers((prev) => ({ ...prev, [id]: value }));

  const readonlyAnswerFor = (question: IApplicantQuestion, index: number) => {
    if (!readOnly || answerRows[index] == null) return undefined;

    return getAnswerValue(question, index, answerRows);
  };

  const answersAttributes = useMemo(() => {
    const attrs: Record<number, unknown> = {};
    questions.forEach((question, index) => {
      attrs[index] = getAnswerAttributes(question, index, answers, {});
    });
    return attrs;
  }, [questions, answers]);

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (questions.length === 0) {
      return (
        <Typography sx={{ color: palette.headingNeutral, fontFamily: font }}>
          This form has no questions to answer yet. Please check back later or contact your
          recruiter if you believe this is an error.
        </Typography>
      );
    }

    return (
      <Box sx={FIELD_BORDER_SX}>
        {questions.map((question, index) => (
          <Box
            key={question.id}
            ref={(el: HTMLDivElement) => {
              questionsRef.current[index] = el;
            }}
            sx={{ mb: 3 }}
          >
            {question.type === 'SelectField' ? (
              <PortalSelectQuestion
                answer={readonlyAnswerFor(question, index)}
                disabled={readOnly}
                error={questionErrors[question.id]}
                onChange={setAnswer}
                question={question}
              />
            ) : (
              <QuestionFieldGenerator
                fullWidth
                answer={readonlyAnswerFor(question, index)}
                disabled={readOnly}
                onChangeCallback={setAnswer}
                question={question}
                questionError={questionErrors[question.id]}
              />
            )}
          </Box>
        ))}
        <PortalFooter>
          <Button buttonVariant="text" id="portal-cancel-form-button" onClick={onBack}>
            {readOnly ? 'Back' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button
              disabled={loading || submitting || formId == null || questions.length === 0}
              loading={submitting}
              onClick={async () => {
                if (!validateForm(questions, answers, setQuestionErrors, questionsRef)) {
                  return;
                }
                setSubmitting(true);
                try {
                  if (formId == null) return;
                  await onSubmit({
                    form_id: formId,
                    answers_attributes: answersAttributes
                  });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              Submit
            </Button>
          )}
        </PortalFooter>
      </Box>
    );
  };

  return (
    <Layout title={item.label}>
      <PortalCard>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 24
          }}
        >
          <PortalBackButton onClick={onBack} />
          {readOnly ? (
            <Button
              buttonVariant="outline"
              icon={<FileDownloadIcon />}
              id="portal-download-form-button"
              onClick={downloadForm}
            >
              Download form
            </Button>
          ) : null}
        </div>
        {renderBody()}
      </PortalCard>
    </Layout>
  );
};

export default SmartForm;
